import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Field } from "../components/Field";
import { CatchFields, CatchFormValue } from "../components/CatchFields";
import { formatDateTime, toDateTimeInput, weightLabel } from "../lib/format";

export default function CatchDetailsPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const catchQuery = useQuery({
    queryKey: ["carpintel_catch", id],
    queryFn: () =>
      fetch(`/app-api/catches/${id}`).then((r) => {
        if (!r.ok) throw new Error("Catch not found");
        return r.json();
      }),
  });
  const sessionsQuery = useQuery({
    queryKey: ["carpintel_sessions"],
    queryFn: () => fetch("/app-api/sessions").then((r) => r.json()),
  });
  const emptyBait = () => ({
    canonical_id: null as string | null,
    user_bait_id: null as number | null,
    snapshot: "",
  });
  const emptyRig = () => ({
    canonical_id: null as string | null,
    user_rig_id: null as number | null,
    snapshot: "",
  });
  const [values, setValues] = useState<CatchFormValue>({
    session_id: "",
    session_angler_id: "",
    catch_species: "carp",
    peg: "",
    caught_at: "",
    species: "",
    carp_type: "Unknown",
    weight_lb: "",
    weight_oz: "",
    rod_number: "",
    rod_setup_id: "",
    spot: "",
    spot_id: "",
    distance_m: "",
    depth_ft: "",
    rig_selection: emptyRig(),
    rig: "",
    feed_bait_selection: emptyBait(),
    main_bait: "",
    hookbait_selection: emptyBait(),
    hookbait: "",
    additive_selection: emptyBait(),
    additive: "",
    baiting_approach: "",
    bait_amount: "",
    bait_unit: "grams",
    bait_notes: "",
    privacy_setting: "Contribute anonymously to CarpIntel statistics",
    share_peg_anonymously: false,
    catch_notes: "",
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const catchRow = catchQuery.data;
  useEffect(() => {
    if (catchRow)
      setValues({
        session_id: String(catchRow.session_id),
        session_angler_id: catchRow.session_angler_id
          ? String(catchRow.session_angler_id)
          : "",
        catch_species: catchRow.catch_species || "carp",
        peg: catchRow.peg || "",
        caught_at: toDateTimeInput(catchRow.caught_at),
        species: catchRow.species || "",
        carp_type: catchRow.carp_type || "Unknown",
        weight_lb: catchRow.weight_lb ?? catchRow.weight_lbs ?? "",
        weight_oz: catchRow.weight_oz ?? "",
        rod_number: catchRow.rod_number ?? "",
        rod_setup_id: catchRow.rod_setup_id ? String(catchRow.rod_setup_id) : "",
        spot: catchRow.spot || "",
        spot_id: catchRow.spot_id ? String(catchRow.spot_id) : "",
        distance_m: catchRow.distance_m ?? "",
        depth_ft: catchRow.depth_ft ?? "",
        rig_selection: {
          canonical_id: catchRow.canonical_rig_id ?? null,
          user_rig_id: catchRow.user_rig_id ?? null,
          snapshot: catchRow.rig_snapshot || catchRow.rig || "",
        },
        rig: catchRow.rig_snapshot || catchRow.rig || "",
        feed_bait_selection: {
          canonical_id: catchRow.canonical_feed_bait_id ?? null,
          user_bait_id: catchRow.user_feed_bait_id ?? null,
          snapshot: catchRow.feed_bait_snapshot || catchRow.main_bait || "",
        },
        main_bait: catchRow.feed_bait_snapshot || catchRow.main_bait || "",
        hookbait_selection: {
          canonical_id: catchRow.canonical_hookbait_id ?? null,
          user_bait_id: catchRow.user_hookbait_id ?? null,
          snapshot: catchRow.hookbait_snapshot || catchRow.hookbait || "",
        },
        hookbait: catchRow.hookbait_snapshot || catchRow.hookbait || "",
        additive_selection: {
          canonical_id: catchRow.canonical_additive_bait_id ?? null,
          user_bait_id: catchRow.user_additive_bait_id ?? null,
          snapshot: catchRow.additive_snapshot || catchRow.additive || "",
        },
        additive: catchRow.additive_snapshot || catchRow.additive || "",
        baiting_approach: catchRow.baiting_approach || "",
        bait_amount: catchRow.bait_amount ?? catchRow.bait_amount_kg ?? "",
        bait_unit:
          catchRow.bait_unit ||
          (catchRow.bait_amount_kg != null ? "kilograms" : "grams"),
        bait_notes: catchRow.bait_notes || "",
        privacy_setting:
          catchRow.privacy_setting ||
          "Contribute anonymously to CarpIntel statistics",
        share_peg_anonymously: Boolean(catchRow.share_peg_anonymously),
        catch_notes: catchRow.catch_notes || "",
      });
  }, [catchRow]);

  async function saveCatch(event: React.FormEvent) {
    event.preventDefault();
    setMessage("");
    if (
      !values.session_id ||
      !values.peg ||
      !values.caught_at ||
      !values.species
    ) {
      setMessage("Choose a session and add the peg, time and species.");
      return;
    }
    setSaving(true);
    try {
      let photoUrl = catchRow.photo_url || null;
      if (photo) {
        const data = new FormData();
        data.append("file", photo);
        const upload = await fetch("/app-api/photos", {
          method: "POST",
          body: data,
        });
        const uploadResult = await upload.json();
        if (!upload.ok)
          throw new Error(uploadResult.error || "Could not upload photo");
        photoUrl = uploadResult.url;
      }
      const weightEntered =
        String(values.weight_lb ?? "").trim() !== "" ||
        String(values.weight_oz ?? "").trim() !== "";
      const payload = {
        ...values,
        session_id: Number(values.session_id),
        session_angler_id: values.session_angler_id
          ? Number(values.session_angler_id)
          : null,
        caught_at: new Date(values.caught_at).toISOString(),
        weight_lb: weightEntered ? Number(values.weight_lb || 0) : null,
        weight_oz: weightEntered ? Number(values.weight_oz || 0) : null,
        rod_number: values.rod_number || null,
        rod_setup_id: values.rod_setup_id ? Number(values.rod_setup_id) : null,
        distance_m: values.distance_m || null,
        depth_ft: values.depth_ft || null,
        bait_amount: values.bait_amount || null,
        canonical_rig_id: values.rig_selection.canonical_id || null,
        user_rig_id: values.rig_selection.user_rig_id || null,
        rig_snapshot: values.rig_selection.snapshot || values.rig || null,
        canonical_feed_bait_id: values.feed_bait_selection.canonical_id || null,
        user_feed_bait_id: values.feed_bait_selection.user_bait_id || null,
        feed_bait_snapshot:
          values.feed_bait_selection.snapshot || values.main_bait || null,
        canonical_hookbait_id: values.hookbait_selection.canonical_id || null,
        user_hookbait_id: values.hookbait_selection.user_bait_id || null,
        hookbait_snapshot:
          values.hookbait_selection.snapshot || values.hookbait || null,
        canonical_additive_bait_id: values.additive_selection.canonical_id || null,
        user_additive_bait_id: values.additive_selection.user_bait_id || null,
        additive_snapshot:
          values.additive_selection.snapshot || values.additive || null,
        additive: values.additive_selection.snapshot || values.additive || null,
        baiting_approach: values.baiting_approach || null,
        photo_url: photoUrl,
      };
      const response = await fetch(`/app-api/catches/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Could not save changes");
      await queryClient.invalidateQueries({
        queryKey: ["carpintel_catch", id],
      });
      await queryClient.invalidateQueries({ queryKey: ["carpintel_catches"] });
      await queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] });
      await queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] });
      setEditing(false);
      setPhoto(null);
      setMessage("Changes saved");
    } catch (caught) {
      setMessage(
        caught instanceof Error ? caught.message : "Could not save changes",
      );
    }
    setSaving(false);
  }

  if (catchQuery.isLoading || sessionsQuery.isLoading)
    return <PageMessage text="Loading catch…" />;
  if (catchQuery.error || !catchRow)
    return <PageMessage text="That catch could not be found." link="/" />;
  return (
    <div className="mx-auto max-w-[760px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to={`/sessions/${catchRow.session_id}`}
          className="text-sm font-medium text-accent hover:underline"
        >
          ← Back to session · {catchRow.lake_name} · {catchRow.peg}
        </Link>
        <div className="mt-2 flex min-w-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight text-primary">
              {weightLabel(catchRow.weight_lbs, catchRow.weight_oz)}
            </h1>
            <p className="mt-1 truncate text-sm text-secondary">
              {catchRow.species} · {formatDateTime(catchRow.caught_at)}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => setEditing((value) => !value)}
          >
            {editing ? "← Back to catch" : "Edit catch"}
          </Button>
        </div>
      </header>
      {editing ? (
        <form onSubmit={saveCatch} className="space-y-6">
          <CatchFields
            values={values}
            setValues={setValues}
            sessions={sessionsQuery.data ?? []}
          />
          <Field label="Fish photo" id="detail-fish-photo">
            <Input
              id="detail-fish-photo"
              type="file"
              accept="image/*"
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            />
          </Field>
          {message ? (
            <p
              className="rounded-md bg-error-weak p-3 text-sm text-error"
              role="alert"
            >
              {message}
            </p>
          ) : null}
          <div className="flex gap-3">
            <Button type="submit" isLoading={saving}>
              Save changes
            </Button>
            <Button
              type="button"
              variant="tertiary"
              onClick={() => setEditing(false)}
            >
              Back to catch
            </Button>
          </div>
        </form>
      ) : (
        <>
          <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Metric label="Lake" value={catchRow.lake_name} />
            <Metric label="Peg" value={catchRow.peg} />
            <Metric label="Rod" value={catchRow.rod_number || "–"} />
            <Metric label="Carp type" value={catchRow.carp_type || "Unknown"} />
            <Metric label="Rig" value={catchRow.rig || "Not recorded"} />
            <Metric
              label="Privacy"
              value={catchRow.privacy_setting || "Private"}
            />
          </section>
          {catchRow.photo_url ? (
            <img
              src={catchRow.photo_url}
              alt={`${catchRow.species} catch`}
              className="max-h-[420px] w-full rounded-lg border border-border object-cover"
            />
          ) : null}
          <section className="rounded-lg border border-border bg-raised p-4">
            <h2 className="text-lg font-semibold text-primary">
              Spot and bait
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
              <MetricText label="Fishing spot" value={catchRow.spot} />
              <MetricText
                label="Distance"
                value={
                  catchRow.distance_yds != null
                    ? `${catchRow.distance_yds} yards`
                    : null
                }
              />
              <MetricText
                label="Depth"
                value={
                  catchRow.depth_ft != null ? `${catchRow.depth_ft} feet` : null
                }
              />
              <MetricText label="Main bait" value={catchRow.main_bait} />
              <MetricText label="Hookbait" value={catchRow.hookbait} />
              <MetricText label="Attractor / additive" value={catchRow.additive_snapshot || catchRow.additive} />
              <MetricText
                label="Bait used"
                value={
                  catchRow.bait_amount != null
                    ? `${catchRow.bait_amount} ${catchRow.bait_unit || "grams"}`
                    : null
                }
              />
              <MetricText label="Bait notes" value={catchRow.bait_notes} />
            </div>
          </section>
          {catchRow.catch_notes ? (
            <p className="rounded-lg border border-border bg-raised p-4 text-sm italic leading-6 text-secondary">
              "{catchRow.catch_notes}"
            </p>
          ) : null}
          {/* Conditions at catch — auto-captured environmental snapshot */}
          {catchRow.weather_condition ||
          catchRow.catch_air_temp_c != null ||
          catchRow.moon_phase ? (
            <section className="rounded-lg border border-border bg-raised p-4">
              <h2 className="text-base font-semibold text-primary">
                Conditions at catch
              </h2>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
                {catchRow.weather_condition ? (
                  <MetricText
                    label="Weather"
                    value={catchRow.weather_condition}
                  />
                ) : null}
                {catchRow.catch_air_temp_c != null ? (
                  <MetricText
                    label="Air"
                    value={`${catchRow.catch_air_temp_c}°C`}
                  />
                ) : null}
                {catchRow.catch_air_pressure_hpa != null ? (
                  <MetricText
                    label="Pressure"
                    value={`${catchRow.catch_air_pressure_hpa} hPa${catchRow.catch_pressure_trend ? ` · ${catchRow.catch_pressure_trend}` : ""}`}
                  />
                ) : null}
                {catchRow.catch_wind_direction ||
                catchRow.catch_wind_speed_mph != null ? (
                  <MetricText
                    label="Wind"
                    value={[
                      catchRow.catch_wind_direction,
                      catchRow.catch_wind_speed_mph != null
                        ? `${catchRow.catch_wind_speed_mph} mph`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />
                ) : null}
                {catchRow.catch_cloud_cover_pct != null ? (
                  <MetricText
                    label="Cloud"
                    value={`${catchRow.catch_cloud_cover_pct}%`}
                  />
                ) : null}
                {catchRow.catch_precipitation_mm != null ? (
                  <MetricText
                    label="Rain"
                    value={`${catchRow.catch_precipitation_mm} mm`}
                  />
                ) : null}
                {catchRow.moon_phase ? (
                  <MetricText
                    label="Moon"
                    value={`${catchRow.moon_phase}${catchRow.moon_illumination_pct != null ? ` · ${catchRow.moon_illumination_pct}%` : ""}`}
                  />
                ) : null}
                {catchRow.moonrise_at ? (
                  <MetricText
                    label="Moonrise"
                    value={new Date(catchRow.moonrise_at).toLocaleTimeString(
                      "en-GB",
                      { hour: "2-digit", minute: "2-digit" },
                    )}
                  />
                ) : null}
                {catchRow.moonset_at ? (
                  <MetricText
                    label="Moonset"
                    value={new Date(catchRow.moonset_at).toLocaleTimeString(
                      "en-GB",
                      { hour: "2-digit", minute: "2-digit" },
                    )}
                  />
                ) : null}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-raised p-3">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-primary">
        {value}
      </p>
    </div>
  );
}
function MetricText({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-1 truncate font-medium text-primary">
        {value || "Not recorded"}
      </p>
    </div>
  );
}
function PageMessage({ text, link }: { text: string; link?: string }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-secondary">
      <p>{text}</p>
      {link ? (
        <Link
          to={link}
          className="mt-3 inline-block font-medium text-accent hover:underline"
        >
          Back to your log
        </Link>
      ) : null}
    </div>
  );
}
